import { useMemo, memo } from 'react';
import type * as d3 from 'd3';
import { format } from 'date-fns';
import type { Sprint, ZoomLevel } from '@/types/sprint';
import { isSprintInRange, isCurrentSprint } from '@/utils/sprintUtils';

interface SprintBoundariesProps {
  xScale: d3.ScaleTime<number, number>;
  height: number;
  sprints: Sprint[];
  timelineRange: { start: Date; end: Date };
  zoomLevel: ZoomLevel;
}

/**
 * SprintBoundaries - Renders sprint boundary regions as SVG elements.
 *
 * Must be placed as the first child `<g>` inside an SVG so that tasks render
 * on top (SVG paints in DOM order — later elements appear above earlier ones).
 *
 * Accepts a D3 ScaleTime to map sprint dates → pixel positions, ensuring
 * perfect alignment with the host timeline's axis.
 *
 * When the sprints array is empty, renders a single hint text element instead
 * of sprint regions.
 *
 * Satisfies AC: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14
 */
const SprintBoundariesComponent = ({
  xScale,
  height,
  sprints,
  timelineRange,
  zoomLevel,
}: SprintBoundariesProps) => {
  // Derive width safely — xScale.range() can return the scale itself in test
  // environments that mock D3 with `.range: vi.fn().mockReturnThis()`.
  const rangeResult = xScale.range();
  const [rangeMin, rangeMax]: [number, number] = Array.isArray(rangeResult)
    ? [rangeResult[0] ?? 0, rangeResult[1] ?? 1000]
    : [0, 1000];
  const width = rangeMax - rangeMin;

  // Viewport culling: keep sprints that overlap the visible timeline range AND whose
  // pixel extent intersects the viewport [-50, width+50]. Using both startX and endX
  // ensures sprints that began before the visible window but extend into it are rendered.
  const visibleSprints = useMemo(() => {
    if (!sprints || sprints.length === 0) return [];
    return sprints.filter(sprint => {
      if (!isSprintInRange(sprint, timelineRange)) return false;
      const startX = xScale(sprint.start_date);
      const endX = xScale(sprint.end_date);
      return endX >= -50 && startX <= width + 50;
    });
  }, [sprints, timelineRange, xScale, width]);

  // Hides labels at Day zoom level (too crowded). Shown at week/sprint/month.
  const showLabels = zoomLevel !== 'day';

  // Compute current sprint once, outside the render map, to avoid N * new Date() allocations per render.
  // Must be called before any early returns to satisfy Rules of Hooks.
  const currentSprintId = useMemo(() => sprints.find(isCurrentSprint)?.id ?? null, [sprints]);

  // Empty state: no sprints imported yet (AC14)
  if (!sprints || sprints.length === 0) {
    return (
      <text
        x={width / 2}
        y={height / 2}
        textAnchor="middle"
        fill="#9CA3AF"
        fontSize="13"
        fontStyle="italic"
        className="sprint-empty-state"
        aria-label="No sprint data available"
      >
        Import sprint calendar to see boundaries
      </text>
    );
  }

  return (
    <g className="sprint-boundaries" aria-label="Sprint boundary regions">
      {visibleSprints.map((sprint) => {
        const startX = xScale(sprint.start_date);
        const endX = xScale(sprint.end_date);
        const sprintWidth = Math.max(0, endX - startX);
        const isCurrent = sprint.id === currentSprintId;
        // Use sprint_number (1-based) for a stable alternating pattern that doesn't
        // shift when viewport culling removes early sprints from the visible list.
        const isAlternate = sprint.sprint_number % 2 === 0;

        // Background fill: blue-50 for current sprint, gray-50 for alternating, white for rest (AC9, AC12)
        let bgFill = 'white';
        if (isCurrent) {
          bgFill = '#EFF6FF'; // blue-50
        } else if (isAlternate) {
          bgFill = '#F9FAFB'; // gray-50
        }

        const labelText = `${sprint.name} • ${format(sprint.start_date, 'MMM d')}–${format(sprint.end_date, 'MMM d')}`;
        // Limit label to available width (approx 7px per char)
        const maxChars = Math.floor((sprintWidth - 16) / 7);
        const truncatedLabel =
          labelText.length > maxChars && maxChars > 5
            ? labelText.substring(0, maxChars - 1) + '…'
            : labelText;

        return (
          <g key={sprint.id} className="sprint-boundary">
            {/* Alternating background region (AC9) */}
            <rect
              x={startX}
              y={0}
              width={sprintWidth}
              height={height}
              fill={bgFill}
              opacity={isCurrent ? 0.9 : 0.6}
              aria-label={`${sprint.name} background`}
            />

            {/* Dashed blue-300 vertical line at sprint start (AC10) */}
            <line
              x1={startX}
              y1={0}
              x2={startX}
              y2={height}
              stroke="#93C5FD"
              strokeWidth={1.5}
              strokeDasharray="4,3"
              className="sprint-start-line"
            />

            {/* Sprint label badge at top (AC10, AC11) — hidden at Day zoom */}
            {showLabels && sprintWidth >= 60 && (
              <g className="sprint-label-badge" aria-label={labelText}>
                <rect
                  x={startX + 3}
                  y={3}
                  width={Math.min(sprintWidth - 6, truncatedLabel.length * 7 + 10)}
                  height={18}
                  rx={3}
                  fill="white"
                  fillOpacity={0.92}
                  stroke="#93C5FD"
                  strokeWidth={0.5}
                />
                <text
                  x={startX + 8}
                  y={16}
                  fontSize="10"
                  fill="#3B82F6"
                  fontWeight="600"
                  className="sprint-label-text"
                >
                  {truncatedLabel}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
};

export const SprintBoundaries = memo(SprintBoundariesComponent);
